import { eventNames } from 'constants/eventNames';
import TrackingEvent from 'constants/trackingEvents';
import PubSub from 'pubsub-js';
import { useTranslation } from 'react-i18next';
import { AvatarSize } from 'types';
import { USER_AVATAR_COLORS } from 'utils/constants';
import { rawId } from 'utils/rawId';

import React, { useRef } from 'react';

import { Avatar } from '@thinkific/chalk/Avatar';
import { classNames } from '@thinkific/chalk/utils';

import { useCommunityContext } from 'components/CommunityContext';

import * as css from './UserAvatar.css';

interface UserAvatarProps {
  alt?: string | null;
  avatarUrl?: string | null;
  className?: string;
  parentClassName?: string;
  username: string;
  userId: string;
  size: AvatarSize;
  shouldOpenProfile?: boolean;
  shouldOpenSideMenu?: boolean;
  showOnlineIndicator?: boolean;
  action?: React.ReactNode;
}

export const UserAvatar = ({
  avatarUrl,
  className,
  parentClassName,
  username,
  userId,
  size,
  showOnlineIndicator = false,
  shouldOpenProfile = false,
  shouldOpenSideMenu = false,
  alt,
  action = null,
}: UserAvatarProps) => {
  const { trackEvent } = useCommunityContext();
  const userAvatarRef = useRef(null);
  const { t } = useTranslation();

  const USER_DEFAULT_AVATAR_URL = '/assets/tenant/defaults/avatar.png';
  const lastDigitOfUserId: number = (rawId(userId) || 0) % USER_AVATAR_COLORS.length;
  const isDefaultAvatar = avatarUrl === USER_DEFAULT_AVATAR_URL;
  type ChalkAvatarSize = 'xs' | 'sm' | 'lg' | 'xl';
  const sizeMap: Record<AvatarSize, ChalkAvatarSize> = {
    xs: 'xs',
    sm: 'sm',
    md: 'lg',
    lg: 'xl',
  };

  function handleUserDetailAction(event) {
    event.preventDefault();
    event.stopPropagation();
    PubSub.publish(eventNames.OPEN_PROFILE_PANEL, {
      userId,
      lastElementRef: userAvatarRef,
    });

    trackEvent(TrackingEvent.PROFILE_PANEL_TAB_OPENED);
  }

  const handleSideBarAction = (event) => {
    event.preventDefault();
    event.stopPropagation();
    PubSub.publish(eventNames.OPEN_SIDE_MENU, {
      userId,
      lastElementRef: userAvatarRef,
    });
  };

  const sideBarProps = shouldOpenSideMenu
    ? {
        className: css.globalHeader,
        onClick: handleSideBarAction,
      }
    : {};
  const extraProps = shouldOpenProfile
    ? {
        className: css.container,
        'aria-label': t('Open profile tab'),
        'aria-hidden': false,
        ref: userAvatarRef,
        role: 'button',
        tabIndex: 0,
        onClick: handleUserDetailAction,
        onKeyDown: (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleUserDetailAction(event);
          }
        },
      }
    : { className: css.container };

  return (
    <div
      data-qa="user-avatar__container"
      aria-hidden
      {...extraProps}
      {...sideBarProps}
      className={classNames(
        css.container?.[shouldOpenProfile || shouldOpenSideMenu ? 'clickable' : 'base'],
        parentClassName
      )}
    >
      <Avatar
        a11yLabel={alt || ''}
        backgroundColor={USER_AVATAR_COLORS[lastDigitOfUserId]}
        className={classNames(showOnlineIndicator && css.mask[sizeMap[size]], className)}
        name={username}
        size={sizeMap[size]}
        src={isDefaultAvatar ? '' : avatarUrl || ''}
      />
      {showOnlineIndicator && (
        <span className={classNames(css.onlineIndicator, css.indicatorSize[sizeMap[size]])} />
      )}
      {action}
    </div>
  );
};

export default UserAvatar;
